/** Concrete repositories, one per entity. */

import { openJson, sealJson } from '@/crypto/primitives';
import type { AccountRecord } from '@/crypto/vault';
import { formatDate } from '@/domain/dates';
import {
  DEFAULT_APP_SETTINGS,
  DEFAULT_COMPANY_SETTINGS,
  type Address,
  type AppSettings,
  type CompanySettings,
  type Invoice,
  type Order,
  type Passenger,
  type Shift,
  type Trip,
  type Vehicle,
} from '@/domain/types';
import { appendAudit, diffRecords, type AuditContext } from './audit';
import { SealedRepository } from './repository';
import {
  PAYLOAD_VERSION,
  STATIC_BUCKET,
  accountsDb,
  db,
  monthBucket,
  type Envelope,
  type InvoiceEnvelope,
  type OrderEnvelope,
  type ShiftEnvelope,
  type TripEnvelope,
} from './schema';

export const tripRepo = new SealedRepository<Trip, TripEnvelope>({
  table: () => db.trips,
  entityType: 'trip',
  bucketOf: (trip) => monthBucket(trip.date),
  indexOf: (trip) => ({
    shiftId: trip.shiftId,
    invoiceId: trip.invoiceId ?? null,
    date: trip.date,
    lockedAt: trip.lockedAt ?? null,
  }),
  labelOf: (trip) => `Fahrt vom ${formatDate(trip.date)}`,
});

export const shiftRepo = new SealedRepository<Shift, ShiftEnvelope>({
  table: () => db.shifts,
  entityType: 'shift',
  bucketOf: (shift) => monthBucket(shift.date),
  indexOf: (shift) => ({ date: shift.date, closedAt: shift.closedAt ?? null }),
  labelOf: (shift) => `Schicht vom ${formatDate(shift.date)}`,
});

export const orderRepo = new SealedRepository<Order, OrderEnvelope>({
  table: () => db.orders,
  entityType: 'order',
  bucketOf: (order) => monthBucket(order.receivedAt.slice(0, 10)),
  indexOf: (order) => ({
    tripId: order.tripId ?? null,
    sequenceNumber: order.sequenceNumber,
    year: order.receivedAt.slice(0, 4),
  }),
  labelOf: (order) =>
    `Auftragseingang Nr. ${order.sequenceNumber} vom ${formatDate(order.receivedAt.slice(0, 10))}${
      order.ordererName ? ` — ${order.ordererName}` : ''
    }`,
});

export const passengerRepo = new SealedRepository<Passenger, Envelope>({
  table: () => db.passengers,
  entityType: 'passenger',
  bucketOf: () => STATIC_BUCKET,
  labelOf: (passenger) =>
    [passenger.firstName, passenger.lastName].filter(Boolean).join(' ') || passenger.company || '—',
});

export const addressRepo = new SealedRepository<Address, Envelope>({
  table: () => db.addresses,
  entityType: 'address',
  bucketOf: () => STATIC_BUCKET,
  labelOf: (address) => address.label,
});

export const vehicleRepo = new SealedRepository<Vehicle, Envelope>({
  table: () => db.vehicles,
  entityType: 'vehicle',
  bucketOf: () => STATIC_BUCKET,
  labelOf: (vehicle) => `${vehicle.vehicleCode} (${vehicle.licensePlate})`,
});

export const invoiceRepo = new SealedRepository<Invoice, InvoiceEnvelope>({
  table: () => db.invoices,
  entityType: 'invoice',
  bucketOf: (invoice) => monthBucket(invoice.issueDate),
  indexOf: (invoice) => ({
    invoiceNumber: invoice.invoiceNumber,
    numberCircle: invoice.numberCircle,
    status: invoice.status,
    issueDate: invoice.issueDate,
  }),
  labelOf: (invoice) => `Rechnung ${invoice.invoiceNumber}`,
});

// ---------------------------------------------------------------------------
// Trip queries
// ---------------------------------------------------------------------------

export async function tripsInPeriod(
  dataKey: CryptoKey,
  fromIso: string,
  toIso: string,
): Promise<Trip[]> {
  const envelopes = await db.trips.where('date').between(fromIso, toIso, true, true).toArray();
  const trips = await Promise.all(
    envelopes.map((e) => openJson<Trip>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return trips.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
}

export async function tripsForShift(dataKey: CryptoKey, shiftId: string): Promise<Trip[]> {
  const envelopes = await db.trips.where('shiftId').equals(shiftId).toArray();
  const trips = await Promise.all(
    envelopes.map((e) => openJson<Trip>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return trips.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
}

export async function tripsForInvoice(dataKey: CryptoKey, invoiceId: string): Promise<Trip[]> {
  const envelopes = await db.trips.where('invoiceId').equals(invoiceId).toArray();
  return Promise.all(envelopes.map((e) => openJson<Trip>(dataKey, { iv: e.iv, ct: e.ct })));
}

/** Trips in the period that have not yet been put on an invoice. */
export async function uninvoicedTrips(
  dataKey: CryptoKey,
  fromIso: string,
  toIso: string,
): Promise<Trip[]> {
  const trips = await tripsInPeriod(dataKey, fromIso, toIso);
  return trips.filter((trip) => !trip.invoiceId);
}

export async function recentTrips(dataKey: CryptoKey, limit: number): Promise<Trip[]> {
  const envelopes = await db.trips.orderBy('date').reverse().limit(limit).toArray();
  const trips = await Promise.all(
    envelopes.map((e) => openJson<Trip>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return trips.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

// ---------------------------------------------------------------------------
// Shift queries
// ---------------------------------------------------------------------------

export async function shiftForDate(
  dataKey: CryptoKey,
  date: string,
  driverId: string,
): Promise<Shift | undefined> {
  const envelopes = await db.shifts.where('date').equals(date).toArray();
  for (const envelope of envelopes) {
    const shift = await openJson<Shift>(dataKey, { iv: envelope.iv, ct: envelope.ct });
    if (shift.driverId === driverId) return shift;
  }
  return undefined;
}

export async function openShift(dataKey: CryptoKey, driverId: string): Promise<Shift | undefined> {
  const envelopes = await db.shifts.where('closedAt').equals('').toArray();
  // Dexie cannot index null, so the open shift is found by scanning the
  // handful of rows whose `closedAt` column is null.
  const candidates = envelopes.length
    ? envelopes
    : (await db.shifts.toArray()).filter((e) => e.closedAt === null);
  const shifts = await Promise.all(
    candidates.map((e) => openJson<Shift>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return shifts
    .filter((shift) => shift.driverId === driverId && !shift.closedAt)
    .sort((a, b) => b.date.localeCompare(a.date))[0];
}

export async function shiftsInPeriod(
  dataKey: CryptoKey,
  fromIso: string,
  toIso: string,
): Promise<Shift[]> {
  const envelopes = await db.shifts.where('date').between(fromIso, toIso, true, true).toArray();
  const shifts = await Promise.all(
    envelopes.map((e) => openJson<Shift>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return shifts.sort((a, b) => a.date.localeCompare(b.date));
}

// ---------------------------------------------------------------------------
// Order queries
// ---------------------------------------------------------------------------

export async function unlinkedOrders(dataKey: CryptoKey): Promise<Order[]> {
  const envelopes = (await db.orders.toArray()).filter((e) => e.tripId === null);
  const orders = await Promise.all(
    envelopes.map((e) => openJson<Order>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return orders.sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
}

export async function ordersInPeriod(
  dataKey: CryptoKey,
  fromIso: string,
  toIso: string,
): Promise<Order[]> {
  const orders = await orderRepo.all(dataKey);
  return orders
    .filter((order) => {
      const date = order.receivedAt.slice(0, 10);
      return date >= fromIso && date <= toIso;
    })
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
}

/**
 * Highest `Lfd. Nr.` already issued in a calendar year, or 0 for an empty year.
 *
 * Read off the clear index rather than the payloads: the next number has to be
 * available before anything is decrypted, and an auditor counting rows must
 * arrive at the same answer we do.
 */
export async function highestOrderSequence(year: string): Promise<number> {
  const last = await db.orders
    .where('[year+sequenceNumber]')
    .between([year, 0], [year, Number.MAX_SAFE_INTEGER])
    .last();
  return last?.sequenceNumber ?? 0;
}

/** Every `Lfd. Nr.` issued in a year, ascending — the input for a gap check. */
export async function orderSequencesInYear(year: string): Promise<number[]> {
  const envelopes = await db.orders.where('year').equals(year).toArray();
  return envelopes.map((envelope) => envelope.sequenceNumber).sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Invoice queries
// ---------------------------------------------------------------------------

export async function invoicesInPeriod(
  dataKey: CryptoKey,
  fromIso: string,
  toIso: string,
): Promise<Invoice[]> {
  const envelopes = await db.invoices
    .where('issueDate')
    .between(fromIso, toIso, true, true)
    .toArray();
  const invoices = await Promise.all(
    envelopes.map((e) => openJson<Invoice>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return invoices.sort((a, b) => a.invoiceNumber.localeCompare(b.invoiceNumber, 'de'));
}

export async function openInvoices(dataKey: CryptoKey): Promise<Invoice[]> {
  const envelopes = await db.invoices.where('status').anyOf(['offen', 'ueberfaellig']).toArray();
  const invoices = await Promise.all(
    envelopes.map((e) => openJson<Invoice>(dataKey, { iv: e.iv, ct: e.ct })),
  );
  return invoices.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

export async function allInvoiceNumbers(circle: string): Promise<string[]> {
  const envelopes = await db.invoices.where('numberCircle').equals(circle).toArray();
  return envelopes.map((e) => e.invoiceNumber).sort((a, b) => a.localeCompare(b, 'de'));
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

const COMPANY_SETTINGS_ID = 'company';
const APP_SETTINGS_ID = 'app';

async function readSettings<T>(dataKey: CryptoKey, id: string, fallback: T): Promise<T> {
  const envelope = await db.settings.get(id);
  if (!envelope) return fallback;
  const stored = await openJson<Partial<T>>(dataKey, { iv: envelope.iv, ct: envelope.ct });
  return { ...fallback, ...stored };
}

async function writeSettings<T extends object>(
  ctx: AuditContext,
  id: string,
  next: T,
  previous: T,
): Promise<void> {
  const sealed = await sealJson(ctx.dataKey, next);
  const envelope: Envelope = {
    id,
    bucket: STATIC_BUCKET,
    iv: sealed.iv,
    ct: sealed.ct,
    pv: PAYLOAD_VERSION,
  };
  await db.transaction('rw', [db.settings, db.auditLog], async () => {
    await db.settings.put(envelope);
    await appendAudit(ctx, {
      action: 'update',
      entityType: 'settings',
      entityId: id,
      entityLabel: id === COMPANY_SETTINGS_ID ? 'Firmendaten' : 'App-Einstellungen',
      changes: diffRecords(
        previous as unknown as Record<string, unknown>,
        next as unknown as Record<string, unknown>,
      ),
      before: previous,
      after: next,
    });
  });
}

export async function getCompanySettings(dataKey: CryptoKey): Promise<CompanySettings> {
  return readSettings(dataKey, COMPANY_SETTINGS_ID, DEFAULT_COMPANY_SETTINGS);
}

export async function saveCompanySettings(
  ctx: AuditContext,
  next: CompanySettings,
): Promise<void> {
  const previous = await getCompanySettings(ctx.dataKey);
  await writeSettings(ctx, COMPANY_SETTINGS_ID, next, previous);
}

export async function getAppSettings(dataKey: CryptoKey): Promise<AppSettings> {
  return readSettings(dataKey, APP_SETTINGS_ID, DEFAULT_APP_SETTINGS);
}

export async function saveAppSettings(ctx: AuditContext, next: AppSettings): Promise<void> {
  const previous = await getAppSettings(ctx.dataKey);
  await writeSettings(ctx, APP_SETTINGS_ID, next, previous);
}

/**
 * Record a backup without going through the settings diff, so the audit log
 * gets a `backup` action rather than a noisy field change.
 */
export async function recordBackupTimestamp(ctx: AuditContext, at: string): Promise<void> {
  const settings = await getAppSettings(ctx.dataKey);
  const next: AppSettings = { ...settings, lastBackupAt: at };
  const sealed = await sealJson(ctx.dataKey, next);
  await db.transaction('rw', [db.settings, db.auditLog], async () => {
    await db.settings.put({
      id: APP_SETTINGS_ID,
      bucket: STATIC_BUCKET,
      iv: sealed.iv,
      ct: sealed.ct,
      pv: PAYLOAD_VERSION,
    });
    await appendAudit(ctx, {
      action: 'backup',
      entityType: 'system',
      entityId: APP_SETTINGS_ID,
      entityLabel: 'Datensicherung',
      changes: [],
    });
  });
}

/**
 * Rename the driver or change the Fahrerkennung.
 *
 * The account row sits outside the sealed-envelope machinery — it has to be
 * readable before there is a data key — so it needs its own audited write.
 * The Fahrerkennung appears on every export, which makes a change to it a
 * fact a Betriebsprüfer may reasonably want explained.
 *
 * The registry and the books are different databases, so the two writes cannot
 * share a transaction. The audit entry goes first: if the second write fails
 * the log records a rename that did not take, and the driver simply tries
 * again. The opposite order would lose the provenance of a change that did.
 */
export async function saveDriverIdentity(
  ctx: AuditContext,
  accountId: string,
  changes: { displayName: string; driverCode: string },
): Promise<AccountRecord> {
  const previous = await accountsDb.accounts.get(accountId);
  if (!previous) throw new Error('Konto nicht gefunden.');

  const next: AccountRecord = {
    ...previous,
    displayName: changes.displayName.trim(),
    driverCode: changes.driverCode.trim(),
  };

  await db.transaction('rw', [db.auditLog], async () => {
    await appendAudit(ctx, {
      action: 'update',
      entityType: 'driver',
      entityId: accountId,
      entityLabel: next.displayName,
      changes: diffRecords(
        { displayName: previous.displayName, driverCode: previous.driverCode },
        { displayName: next.displayName, driverCode: next.driverCode },
      ),
    });
  });
  await accountsDb.accounts.put(next);

  return next;
}
